import React, { useState } from 'react';
import { Table, Button, InputNumber, Modal, Select, message } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';

// GPA-threshold tag tone — mirrors the STATUS_TONE mapping pattern used in
// attendance (§4.5): Bình thường/ok, Cảnh cáo/warn, Đuổi học/bad, else neutral.
const STATE_TONE = { 'Bình thường': 'ok', 'Cảnh cáo': 'warn', 'Đuổi học': 'bad' };

// Standard language-center skills. The four core ones always appear in the
// "add score" picker even when the student has no row for them yet.
const SKILLS_ORDER = ['READING', 'LISTENING', 'WRITING', 'SPEAKING'];

// Score types offered when manually adding a score. Backend rejects duplicates
// (same skill + same type), so over-listing test slots is harmless.
const SCORE_TYPE_OPTIONS = [
    { value: 'midterm', label: 'Giữa kỳ' },
    { value: 'final', label: 'Cuối kỳ' },
    { value: 'test_1', label: 'Kiểm tra 1' },
    { value: 'test_2', label: 'Kiểm tra 2' },
    { value: 'test_3', label: 'Kiểm tra 3' },
    { value: 'test_4', label: 'Kiểm tra 4' },
    { value: 'test_5', label: 'Kiểm tra 5' },
];

// Lớp học thử has exactly one score type. The server rejects entry_test on a non-trial class and
// term scores are meaningless here, so the picker offers only this.
const TRIAL_SCORE_TYPE_OPTIONS = [{ value: 'entry_test', label: 'Điểm đầu vào' }];

export { Scoreboard };

function Scoreboard(props) {
    const [editingKey, setEditingKey] = useState(null);
    const [editValue, setEditValue] = useState(null);
    const [saving, setSaving] = useState(false);

    // "Add score" modal state
    const [addOpen, setAddOpen] = useState(false);
    const [addSkill, setAddSkill] = useState(null);
    const [addType, setAddType] = useState(null);
    const [addValue, setAddValue] = useState(null);
    const [adding, setAdding] = useState(false);

    const startEdit = (scoreId, currentScore) => {
        setEditingKey(scoreId);
        setEditValue(currentScore);
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setEditValue(null);
    };

    const saveEdit = async (scoreId) => {
        if (editValue === null || editValue === undefined) return;
        setSaving(true);
        try {
            const result = await props.onUpdateScore(scoreId, editValue);
            if (result.status === 'Success') {
                message.success('Cập nhật điểm thành công');
                setEditingKey(null);
                setEditValue(null);
                if (props.onRefresh) await props.onRefresh();
            } else {
                message.error('Lỗi: ' + result.message);
            }
        } catch (e) {
            message.error('Có lỗi xảy ra');
        }
        setSaving(false);
    };

    const openAdd = () => {
        setAddSkill(null);
        setAddType(null);
        setAddValue(null);
        setAddOpen(true);
    };

    const submitAdd = async () => {
        if (!addSkill) { message.warning('Hãy chọn kỹ năng'); return; }
        if (!addType) { message.warning('Hãy chọn loại điểm'); return; }
        if (addValue === null || addValue === undefined) { message.warning('Hãy nhập điểm'); return; }
        setAdding(true);
        try {
            const result = await props.onAddScore({ subject_code: addSkill, score_type: addType, score: addValue });
            if (result && result.status === 'Success') {
                message.success('Thêm điểm thành công');
                setAddOpen(false);
                if (props.onRefresh) await props.onRefresh();
            } else {
                message.error('Lỗi: ' + (result ? result.message : 'không thêm được điểm'));
            }
        } catch (e) {
            message.error('Có lỗi xảy ra');
        }
        setAdding(false);
    };

    function renderScoreCell(entry) {
        if (!entry) return <span className="sc2-empty">—</span>;
        const isEdit = editingKey === entry.score_id;
        if (isEdit) {
            return (
                <span className="sc2-edit">
                    <InputNumber
                        min={0} max={10} step={0.1}
                        value={editValue}
                        onChange={setEditValue}
                        size="small"
                        className="sc2-edit-input"
                    />
                    <button type="button" className="sc2-icon-btn" disabled={saving} onClick={() => saveEdit(entry.score_id)} title="Lưu">
                        <SaveOutlined />
                    </button>
                    <button type="button" className="sc2-icon-btn" onClick={cancelEdit} title="Hủy">
                        <CloseOutlined />
                    </button>
                </span>
            );
        }
        return (
            <span className="sc2-score-cell">
                {entry.score}
                {props.isPersonal === false && (
                    <button
                        type="button" className="sc2-icon-btn sc2-icon-btn-ghost"
                        onClick={() => startEdit(entry.score_id, entry.score)}
                        title="Sửa điểm"
                    >
                        <EditOutlined />
                    </button>
                )}
            </span>
        );
    }

    const scoreboard = props.scoreboard || [];
    const isTrial = !!props.isTrial;
    const maxTestCount = scoreboard.reduce((max, row) => Math.max(max, row.tests ? row.tests.length : 0), 0);

    const testCols = Array.from({ length: maxTestCount }, (_, i) => ({
        title: `Kiểm tra ${i + 1}`,
        key: `test_${i + 1}`,
        width: 130,
        render: (_, record) => renderScoreCell(record.tests && record.tests[i] ? record.tests[i] : null),
    }));

    // Lớp học thử: the only score is the entry test. StudentScore buckets any non-midterm/final type
    // into tests[], so entry_test lands in tests[0] — relabel that one column and drop the term
    // columns, which a trial class never has.
    const columns = isTrial ? [
        { title: 'Kỹ năng', dataIndex: 'subject_name', key: 'subject_name', width: 130 },
        { title: 'Điểm đầu vào', key: 'entry_test', width: 140, render: (_, r) => renderScoreCell(r.tests && r.tests[0] ? r.tests[0] : null) },
    ] : [
        { title: 'Kỹ năng', dataIndex: 'subject_name', key: 'subject_name', width: 130 },
        ...testCols,
        { title: 'Giữa kỳ', key: 'midterm', width: 130, render: (_, r) => renderScoreCell(r.midterm) },
        { title: 'Cuối kỳ', key: 'final', width: 130, render: (_, r) => renderScoreCell(r.final) },
        { title: 'Điểm tổng kết', dataIndex: 'gpa', key: 'gpa', width: 140 },
    ];

    const scoreTotal = props.scoreTotal || {};

    // Staff can manually add scores. Skill options = the four core skills plus
    // any custom skill already present on this student's board.
    const canAdd = props.isPersonal === false && typeof props.onAddScore === 'function';
    const skillNameByCode = {};
    scoreboard.forEach(r => { if (r.subject_code) skillNameByCode[r.subject_code] = r.subject_name; });
    const skillOptions = [
        ...SKILLS_ORDER,
        ...Object.keys(skillNameByCode).filter(c => !SKILLS_ORDER.includes(c)),
    ].map(code => ({ value: code, label: skillNameByCode[code] || code }));

    const stateLabel = Array.isArray(scoreTotal.stt) ? scoreTotal.stt.join(', ') : scoreTotal.stt;
    const stateTone = STATE_TONE[stateLabel] || 'neutral';

    return (
        <div className="sc2-board">
            <div className="sc2-head">
                <div className="sc2-head-l">
                    <h2 className="sc2-name-title">{scoreTotal.name}</h2>
                    <div className="sc2-sub">{scoreTotal.student_id}</div>
                </div>
                {canAdd && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                        Thêm điểm
                    </Button>
                )}
            </div>

            {/* A trial student has one entry test — no GPA, and no đuổi học/cảnh cáo verdict to draw
                from it. Showing either would read as a judgement the centre hasn't made. */}
            {!isTrial &&
                <div className="stat-strip">
                    <span className="stat"><span className="l">GPA</span><span className="v">{scoreTotal.gpa}</span></span>
                    <span className="stat">
                        <span className="l">Trạng thái</span>
                        {stateLabel && <span className={`tag tag-${stateTone}`} style={{ marginLeft: 8 }}>{stateLabel}</span>}
                    </span>
                </div>}
            {props.classPicker && <div className="sc2-classpicker">{props.classPicker}</div>}

            <Table
                className="sc2-table"
                columns={columns}
                dataSource={scoreboard}
                rowKey="key"
                pagination={false}
                scroll={{ x: 'max-content' }}
                summary={() => (
                    <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={3 + maxTestCount}>
                            <span className="sc2-summary-label">GPA tổng quát</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell>
                            <span className="sc2-summary-value">{scoreTotal.gpa || '--'}</span>
                        </Table.Summary.Cell>
                    </Table.Summary.Row>
                )}
            />

            <Modal
                title="Thêm điểm"
                visible={addOpen}
                onCancel={() => setAddOpen(false)}
                onOk={submitAdd}
                okText="Lưu"
                cancelText="Hủy"
                confirmLoading={adding}
            >
                <div className="sc2-form-field">
                    <div className="sc2-form-label">Kỹ năng</div>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Chọn kỹ năng"
                        value={addSkill}
                        onChange={setAddSkill}
                        options={skillOptions}
                    />
                </div>
                <div className="sc2-form-field">
                    <div className="sc2-form-label">Loại điểm</div>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Chọn loại điểm"
                        value={addType}
                        onChange={setAddType}
                        options={isTrial ? TRIAL_SCORE_TYPE_OPTIONS : SCORE_TYPE_OPTIONS}
                    />
                </div>
                <div className="sc2-form-field">
                    <div className="sc2-form-label">Điểm</div>
                    <InputNumber
                        min={0} max={10} step={0.1}
                        value={addValue}
                        onChange={setAddValue}
                        style={{ width: '100%' }}
                        placeholder="0 - 10"
                    />
                </div>
            </Modal>
        </div>
    );
}
